import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuthContext } from '../hooks/useAuthContext';
import { useAddresses } from '../hooks/useAddresses';
import { useOrders } from '../hooks/useOrders';
import { useFavorites } from '../hooks/useFavorites';
import { useLanguage } from '../context/LanguageContext';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { FiChevronDown, FiChevronUp, FiEdit, FiX } from 'react-icons/fi';
import { supabase } from '../config/supabase';
import ProductCard from '../components/ProductCard';
import OrderItemCard from '../components/OrderItemCard';
import ManageAddressesModal from '../components/ManageAddressesModal';

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px;
`;

const TabContainer = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const Tab = styled.button`
  flex: 1;
  padding: 16px;
  border: none;
  background: ${props => props.active ? 'white' : 'transparent'};
  color: ${props => props.active ? '#5efba9' : '#6b7280'};
  font-weight: ${props => props.active ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s;
  border-bottom: ${props => props.active ? '2px solid #5efba9' : '2px solid transparent'};
  
  &:hover {
    background: ${props => props.active ? 'white' : '#f3f4f6'};
  }
`;

const TabContent = styled.div`
  padding: 20px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
`;

const Title = styled.h2`
  margin: 0 0 12px 0;
  font-size: 20px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px dashed #f1f5f9;
  &:last-child { border-bottom: none; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  font-weight: 600;
  padding: 10px 8px;
  border-bottom: 1px solid #e5e7eb;
`;

const Td = styled.td`
  padding: 10px 8px;
  border-bottom: 1px solid #f1f5f9;
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 24px;
  margin-top: 20px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #374151;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 24px;
`;

const ShopButton = styled(Link)`
  display: inline-block;
  padding: 12px 24px;
  background: #5efba9;
  color: #1f2937;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    background: #4de89e;
  }
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const OrderCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  background: white;
`;

const OrderHeader = styled.div`
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  background: #f9fafb;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
  }
`;

const OrderHeaderLeft = styled.div`
  flex: 1;
`;

const OrderHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const OrderDate = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const OrderId = styled.div`
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
`;

const OrderStatus = styled.div`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    if (props.status === 'completed' || props.status === 'delivered') return '#d1fae5';
    if (props.status === 'pending') return '#fef3c7';
    if (props.status === 'cancelled') return '#fee2e2';
    return '#e5e7eb';
  }};
  color: ${props => {
    if (props.status === 'completed' || props.status === 'delivered') return '#065f46';
    if (props.status === 'pending') return '#92400e';
    if (props.status === 'cancelled') return '#991b1b';
    return '#374151';
  }};
`;

const OrderTotal = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
`;

const OrderItems = styled.div`
  padding: 16px;
  border-top: 1px solid #e5e7eb;
  background: white;
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #5efba9;
  color: #1f2937;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 16px;

  &:hover {
    background: #4de89e;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const FormInput = styled.input`
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s;
  outline: none;

  &:focus {
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const FormTextarea = styled.textarea`
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s;
  outline: none;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;

  &:focus {
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: #5efba9;
  color: #1f2937;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #4de89e;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: #f3f4f6;
  color: #374151;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e5e7eb;
  }
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 14px;
  margin-bottom: 16px;
`;

const SuccessMessage = styled.div`
  padding: 12px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  color: #15803d;
  font-size: 14px;
  margin-bottom: 16px;
`;

const Value = styled.span`
  color: #1f2937;
  font-weight: 500;
`;

const EmptyValue = styled.span`
  color: #9ca3af;
  font-style: italic;
`;

const TrackingLink = styled.a`
  display: inline-block;
  margin-top: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #2563eb;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export default function Profile() {
  const { session, isLoggedIn, profile } = useAuthContext();
  const { addresses, loading: addressesLoading } = useAddresses();
  const { orders, loading: ordersLoading, error } = useOrders();
  const { favorites, loading: favoritesLoading } = useFavorites();
  const { isArabic } = useLanguage();
  const [activeTab, setActiveTab] = useState('profile');
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [isEditing, setIsEditing] = useState(false);
  const [profileFormData, setProfileFormData] = useState({
    full_name: '',
  });
  const [profileError, setProfileError] = useState(null);
  const [profileSuccess, setProfileSuccess] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAddressesModal, setShowAddressesModal] = useState(false);

  // Initialize form data when profile loads or editing starts
  useEffect(() => {
    if (profile && isEditing) {
      setProfileFormData({
        full_name: profile.full_name || '',
      });
    }
  }, [profile, isEditing]);

  const handleProfileInputChange = (e) => {
    const { name, value } = e.target;
    setProfileFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    if (!session) return;

    setProfileError(null);
    setProfileSuccess(null);
    setSaving(true);

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          full_name: profileFormData.full_name.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', session.user.id);

      if (updateError) {
        throw updateError;
      }

      setProfileSuccess(isArabic ? 'تم تحديث الملف الشخصي بنجاح' : 'Profile updated successfully');
      setIsEditing(false);
      
      // Refresh the page to get updated profile data
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error('Error updating profile:', err);
      setProfileError(err.message || (isArabic ? 'فشل تحديث الملف الشخصي' : 'Failed to update profile'));
    } finally {
      setSaving(false);
    }
  };

  // Transform favorites to match ProductCard format
  const wishlistItems = favorites.map(fav => {
    const discount = fav.product?.dis && fav.product.dis !== 'no' ? parseFloat(fav.product.dis) : null;
    const discountedPrice = discount && fav.product?.price ? (fav.product.price * (1 - discount / 100)).toFixed(2) : null;
    
    return {
      id: fav.product?.id,
      title: fav.product?.name,
      price: discount ? discountedPrice : fav.product?.price,
      originalPrice: discount ? fav.product?.price : null,
      discount,
      currency: fav.product?.currency,
      image: fav.product?.images?.[0] || fav.product?.img || 'https://via.placeholder.com/300x240',
      brand: fav.product?.brand?.name,
      seller: { id: fav.product?.brand_id || 1, name: fav.product?.brand?.name || 'Seller' }
    };
  }).filter(item => item.id); // Filter out any items with missing product data

  if (!isLoggedIn) {
    return (
      <Container>
        <Card>
          <Title>{isArabic ? 'يرجى تسجيل الدخول' : 'Please sign in'}</Title>
          <p>{isArabic ? 'يجب عليك تسجيل الدخول لعرض ملفك الشخصي.' : 'You need to be signed in to view your profile.'}</p>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <TabContainer>
        <Tabs>
          <Tab active={activeTab === 'profile'} onClick={() => setActiveTab('profile')}>
            {isArabic ? 'الملف الشخصي' : 'Profile'}
          </Tab>
          <Tab active={activeTab === 'orders'} onClick={() => setActiveTab('orders')}>
            {isArabic ? 'الطلبات' : 'Orders'}
          </Tab>
          <Tab active={activeTab === 'wishlist'} onClick={() => setActiveTab('wishlist')}>
            {isArabic ? 'المفضلة' : 'Wishlist'} {favorites.length > 0 && `(${favorites.length})`}
          </Tab>
        </Tabs>

        <TabContent>
          {activeTab === 'profile' && (
            <Grid>
              <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <Title>{isArabic ? 'معلومات الحساب' : 'Account Information'}</Title>
                  {!isEditing && (
                    <EditButton onClick={() => setIsEditing(true)}>
                      <FiEdit /> {isArabic ? 'تعديل' : 'Edit'}
                    </EditButton>
                  )}
                </div>

                {profileError && <ErrorMessage>{profileError}</ErrorMessage>}
                {profileSuccess && <SuccessMessage>{profileSuccess}</SuccessMessage>}

                {!isEditing ? (
                  <>
                    <Row>
                      <span>{isArabic ? 'البريد الإلكتروني' : 'Email'}</span>
                      <Value>{session.user.email}</Value>
                    </Row>
                    <Row>
                      <span>{isArabic ? 'الاسم الكامل' : 'Full Name'}</span>
                      <Value>{profile?.full_name || <EmptyValue>{isArabic ? 'غير محدد' : 'Not set'}</EmptyValue>}</Value>
                    </Row>
                    {profile?.created_at && (
                      <Row>
                        <span>{isArabic ? 'تاريخ الإنشاء' : 'Member Since'}</span>
                        <Value>
                          {new Date(profile.created_at).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </Value>
                      </Row>
                    )}
                  </>
                ) : (
                  <>
                    <FormGroup>
                      <FormLabel>{isArabic ? 'البريد الإلكتروني' : 'Email'}</FormLabel>
                      <FormInput
                        type="email"
                        value={session.user.email}
                        disabled
                        style={{ background: '#f3f4f6', color: '#6b7280' }}
                      />
                      <small style={{ color: '#6b7280', fontSize: '12px' }}>
                        {isArabic ? 'لا يمكن تغيير البريد الإلكتروني' : 'Email cannot be changed'}
                      </small>
                    </FormGroup>

                    <FormGroup>
                      <FormLabel>{isArabic ? 'الاسم الكامل' : 'Full Name'}</FormLabel>
                      <FormInput
                        type="text"
                        name="full_name"
                        value={profileFormData.full_name}
                        onChange={handleProfileInputChange}
                        placeholder={isArabic ? 'أدخل اسمك الكامل' : 'Enter your full name'}
                      />
                    </FormGroup>

                    <ButtonGroup>
                      <SaveButton onClick={handleSaveProfile} disabled={saving}>
                        {saving ? (isArabic ? 'جاري الحفظ...' : 'Saving...') : (isArabic ? 'حفظ' : 'Save')}
                      </SaveButton>
                      <CancelButton onClick={() => {
                        setIsEditing(false);
                        setProfileError(null);
                        setProfileSuccess(null);
                      }}>
                        {isArabic ? 'إلغاء' : 'Cancel'}
                      </CancelButton>
                    </ButtonGroup>
                  </>
                )}
              </Card>

              <Card>
                <Title>{isArabic ? 'العناوين' : 'Addresses'}</Title>
                {addressesLoading && <p>{isArabic ? 'جاري تحميل العناوين...' : 'Loading addresses…'}</p>}
                {!addressesLoading && addresses.length === 0 && (
                  <p>{isArabic ? 'لا توجد عناوين بعد. أضف عنوانًا أثناء الدفع.' : 'No addresses yet. Add one during checkout.'}</p>
                )}
                {!addressesLoading && addresses.map(addr => (
                  <Row key={addr.id}>
                    <span>{addr.full_name || addr.phone_number}</span>
                    <strong>{addr.city}, {addr.governorate}</strong>
                  </Row>
                ))}
                {!addressesLoading && addresses.length > 0 && (
                  <button
                    onClick={() => setShowAddressesModal(true)}
                    style={{
                      display: 'inline-block',
                      marginTop: '16px',
                      color: '#5efba9',
                      background: 'none',
                      border: 'none',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '14px',
                      cursor: 'pointer',
                      padding: 0
                    }}
                  >
                    {isArabic ? 'إدارة العناوين' : 'Manage Addresses'} →
                  </button>
                )}
              </Card>
            </Grid>
          )}

          {activeTab === 'orders' && (
            <div>
              <Title>{isArabic ? 'سجل الطلبات' : 'Order History'}</Title>
              {ordersLoading && <p>{isArabic ? 'جاري تحميل الطلبات...' : 'Loading orders…'}</p>}
              {error && <p style={{color:'#ef4444'}}>{isArabic ? 'خطأ:' : 'Error:'} {String(error)}</p>}
              {!ordersLoading && orders.length === 0 && (
                <EmptyState>
                  <EmptyIcon>📦</EmptyIcon>
                  <EmptyTitle>{isArabic ? 'لا توجد طلبات بعد' : 'No orders yet'}</EmptyTitle>
                  <EmptyText>{isArabic ? 'ابدأ التسوق لرؤية طلباتك هنا' : 'Start shopping to see your orders here'}</EmptyText>
                  <ShopButton to="/">{isArabic ? 'ابدأ التسوق' : 'Start Shopping'}</ShopButton>
                </EmptyState>
              )}
              {!ordersLoading && orders.length > 0 && (
                <OrdersList>
                  {orders.map(order => {
                    const isExpanded = expandedOrders.has(order.id);
                    const orderItems = order.order_items || [];
                    const canRate = order.status === 'completed' || order.status === 'delivered' || order.payment_status === 'paid';
                    const displayStatus = order.shipment_status_name || order.status;

                    return (
                      <OrderCard key={order.id}>
                        <OrderHeader
                          onClick={() => {
                            const newExpanded = new Set(expandedOrders);
                            if (isExpanded) {
                              newExpanded.delete(order.id);
                            } else {
                              newExpanded.add(order.id);
                            }
                            setExpandedOrders(newExpanded);
                          }}
                        >
                          <OrderHeaderLeft>
                            <OrderDate>
                              {order.created_at ? (
                                new Date(order.created_at).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              ) : '-'}
                            </OrderDate>
                            <OrderId>{isArabic ? 'طلب #' : 'Order #'}{order.id.substring(0, 8)}...</OrderId>
                            <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <OrderStatus status={displayStatus}>
                                {isArabic ? (
                                  displayStatus === 'pending' ? 'قيد الانتظار' :
                                  displayStatus === 'paid' ? 'مدفوع' :
                                  displayStatus === 'processed' ? 'قيد المعالجة' :
                                  displayStatus === 'delivered' ? 'تم التوصيل' :
                                  displayStatus === 'completed' ? 'مكتمل' :
                                  displayStatus === 'cancelled' ? 'ملغي' :
                                  displayStatus === 'return_requested' ? 'طلب إرجاع' :
                                  displayStatus === 'refunded' ? 'مسترد' :
                                  displayStatus === 'Shipping Request' ? 'طلب شحن' :
                                  displayStatus === 'Received in the store' ? 'تم الاستلام بالمخزن' :
                                  displayStatus === 'Out To Deliver' ? 'جاري التوصيل' :
                                  displayStatus === 'Delivered To Consignee' ? 'تم التسليم' :
                                  displayStatus
                                ) : displayStatus}
                              </OrderStatus>
                              {order.payment_status && (
                                <OrderStatus 
                                  status={order.payment_status}
                                  style={{ fontSize: '11px', padding: '2px 8px' }}
                                >
                                  {isArabic ? (
                                    order.payment_status === 'pending' ? 'قيد الانتظار' :
                                    order.payment_status === 'paid' ? 'مدفوع' :
                                    order.payment_status === 'failed' ? 'فشل' :
                                    order.payment_status === 'refunded' ? 'مسترد' :
                                    order.payment_status
                                  ) : order.payment_status}
                                </OrderStatus>
                              )}
                            </div>
                            {order.tracking_url && (
                              <TrackingLink href={order.tracking_url} target="_blank" rel="noopener noreferrer">
                                {isArabic ? 'تتبع الشحنة' : 'Track shipment'}
                              </TrackingLink>
                            )}
                          </OrderHeaderLeft>
                          <OrderHeaderRight>
                            <OrderTotal>
                              {Number(order.grand_total || 0).toFixed(2)} {order.currency || 'OMR'}
                            </OrderTotal>
                            {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                          </OrderHeaderRight>
                        </OrderHeader>
                        {isExpanded && (
                          <OrderItems>
                            {orderItems.length === 0 ? (
                              <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
                                {isArabic ? 'لم يتم العثور على عناصر لهذا الطلب' : 'No items found for this order'}
                              </p>
                            ) : (
                              <>
                                {canRate && (
                                  <div style={{ 
                                    marginBottom: '16px', 
                                    padding: '12px', 
                                    background: '#f0fdf4', 
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    color: '#065f46'
                                  }}>
                                    {isArabic ? 'يمكنك تقييم المنتجات من الطلبات المكتملة' : 'You can rate products from completed orders'}
                                  </div>
                                )}
                                {orderItems.map(item => (
                                  <OrderItemCard
                                    key={item.id}
                                    orderItem={item}
                                    order={order}
                                  />
                                ))}
                              </>
                            )}
                          </OrderItems>
                        )}
                      </OrderCard>
                    );
                  })}
                </OrdersList>
              )}
            </div>
          )}

          {activeTab === 'wishlist' && (
            <div>
              <Title>{isArabic ? 'قائمة المفضلة' : 'My Wishlist'}</Title>
              {favoritesLoading && <p>{isArabic ? 'جاري تحميل المفضلة...' : 'Loading wishlist…'}</p>}
              {!favoritesLoading && wishlistItems.length === 0 && (
                <EmptyState>
                  <EmptyIcon>❤️</EmptyIcon>
                  <EmptyTitle>{isArabic ? 'قائمة المفضلة فارغة' : 'Your wishlist is empty'}</EmptyTitle>
                  <EmptyText>{isArabic ? 'أضف المنتجات إلى قائمة المفضلة لحفظها لاحقًا.' : 'Add products to your wishlist to save them for later.'}</EmptyText>
                  <ShopButton to="/">{isArabic ? 'ابدأ التسوق' : 'Start Shopping'}</ShopButton>
                </EmptyState>
              )}
              {!favoritesLoading && wishlistItems.length > 0 && (
                <ProductsGrid>
                  {wishlistItems.map(product => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </ProductsGrid>
              )}
            </div>
          )}
        </TabContent>
      </TabContainer>
      
      <ManageAddressesModal
        isOpen={showAddressesModal}
        onClose={() => setShowAddressesModal(false)}
      />
    </Container>
  );
}


